import React from 'react';
import Button from '../../components/Button/Button';
import UICard from '../../styled components/UICard';
import { Body } from '../../styled components/Typography';
import { ToolbarDropdown } from '../toolbar/ListViewToolbar/ListViewToolbar.style';
import ListViewFilterTreeLevel from './ListViewFilterTreeLevel';
import ListViewFilterValues from './ListViewFilterValues';
import type { ListViewFilterEditor } from '../../hooks/useListViewFilterEditor';

/* What the panel is titled: the field being filtered on, else the group drilled
   into, else the panel's own name */
function panelTitle(editor: ListViewFilterEditor): string {
  if (editor.selectedField) return editor.selectedField.name;
  const currentGroup = editor.navStack[editor.navStack.length - 1];
  return currentGroup ? currentGroup.name : i18n('Add filter');
}

interface ListViewFilterPanelProps {
  editor: ListViewFilterEditor;
}

/* The panel the filter is built in: it drills down through the filter tree to a
 * field, and shows the values to filter that field on. Its whole state lives in
 * the editor it is handed. */
const ListViewFilterPanel = ({ editor }: ListViewFilterPanelProps) => (
  <ToolbarDropdown $align="left" style={{ minWidth: '280px' }}>
    <UICard $padding={16} data-test-list-view-filter-panel>
      <div tw="flex items-center justify-between mb-4">
        <div tw="flex items-center">
          {(editor.navStack.length > 0 || editor.selectedField) && (
            <Button
              contentType="icon"
              icon="navigate-before"
              size="small"
              color="default"
              variant="text"
              onClick={editor.goBack}
              aria-label={i18n('Back')}
            />
          )}
          <Body as="h3" $color="black" tw="font-bold m-0">
            {panelTitle(editor)}
          </Body>
        </div>
        <Button
          contentType="icon"
          icon="close"
          size="small"
          color="default"
          variant="text"
          onClick={editor.toggle}
          aria-label={i18n('Close')}
        />
      </div>
      {editor.selectedField ? (
        <>
          <ListViewFilterValues
            field={editor.selectedField}
            usesOptions={editor.usesOptions}
            checkedValues={editor.checkedValues}
            textValue={editor.textValue}
            onToggleValue={editor.toggleValue}
            onChangeText={editor.changeText}
          />
          <div tw="flex justify-end mt-4">
            <Button
              size="small"
              color="primary"
              variant="filled"
              disabled={!editor.canConfirm}
              onClick={editor.confirm}
              dataAttributes={{ 'data-test-list-view-filter-add': true }}
            >
              {editor.isEditing ? i18n('Update') : i18n('Add')}
            </Button>
          </div>
        </>
      ) : (
        <ListViewFilterTreeLevel items={editor.items} onSelectItem={editor.selectItem} />
      )}
    </UICard>
  </ToolbarDropdown>
);

export default ListViewFilterPanel;
